'use client';

import { ChevronDown, Menu, X } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { useState } from 'react';
import { KajianDropdown, MobileMenu, NAV_LINKS, ProductDropdown, SolusiDropdown } from '@/components/landing-navbar-dropdowns';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { useAnalytics } from '@/services/frontendAnalyticsService';

type ActiveLink = 'panduan' | 'harga' | 'kajian' | 'berita' | 'tentang-kami' | 'product' | 'solusi';

interface LandingNavbarProps {
  activeLink?: ActiveLink;
  variant?: 'default' | 'transparent';
}

export default function LandingNavbar({ activeLink, variant = 'default' }: LandingNavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [isSolusiDropdownOpen, setIsSolusiDropdownOpen] = useState(false);
  const [isKajianDropdownOpen, setIsKajianDropdownOpen] = useState(false);
  const { status } = useSession();
  const analytics = useAnalytics();
  const scrollPosition = useScrollPosition();

  const isAuthenticated = status === 'authenticated';

  const scrollThreshold = 100;
  const isScrolled = scrollPosition > scrollThreshold;

  const isTransparent = variant === 'transparent' && !isScrolled;
  const textColor = isTransparent ? 'text-white/80 hover:text-white' : 'text-neutral-600 hover:text-neutral-900';
  const textColorActive = isTransparent ? 'text-white font-semibold' : 'text-neutral-900';
  const logoSrc = isTransparent ? '/static/images/logo-white.png' : '/static/images/logo-black.png';
  const mobileMenuText = isTransparent ? 'text-white' : 'text-neutral-900';
  const mobileMenuHover = isTransparent ? 'hover:bg-white/5' : 'hover:bg-neutral-50';
  const bgClass = isTransparent ? 'bg-transparent' : 'bg-white';
  const shadowClass = isScrolled ? 'shadow-sm' : '';

  const getLinkClass = (link: ActiveLink) => {
    const base = isTransparent
      ? 'text-sm font-medium transition-colors text-white/80 hover:text-white whitespace-nowrap'
      : 'text-sm font-medium transition-colors text-neutral-600 hover:text-neutral-900 whitespace-nowrap';
    if (activeLink === link) {
      return isTransparent ? `${base} text-white font-semibold` : `${base} text-neutral-900`;
    }
    return base;
  };

  return (
    <>
      <nav
        className={`
          fixed top-0 left-0 right-0 z-50 w-full
          ${bgClass} ${shadowClass}
          transition-all duration-300
          px-6 md:px-10 lg:px-12 py-6
          flex items-center justify-between
          md:grid md:grid-cols-[1.4fr_0.6fr_1.2fr] lg:grid-cols-[1.2fr_0.6fr_1.2fr]
        `}
      >
        <div className="hidden md:flex items-center gap-5 lg:gap-8 justify-start">
          <ProductDropdown
            isOpen={isProductDropdownOpen}
            onToggle={setIsProductDropdownOpen}
            textColor={textColor}
            textColorActive={textColorActive}
            activeLink={activeLink}
          />
          <SolusiDropdown
            isOpen={isSolusiDropdownOpen}
            onToggle={setIsSolusiDropdownOpen}
            textColor={textColor}
            textColorActive={textColorActive}
            activeLink={activeLink}
          />
          {NAV_LINKS.map((link) =>
            link.key === 'kajian' ? (
              <KajianDropdown
                key={link.href}
                isOpen={isKajianDropdownOpen}
                onToggle={setIsKajianDropdownOpen}
                textColor={textColor}
                textColorActive={textColorActive}
                activeLink={activeLink}
              />
            ) : (
              <Link key={link.href} href={link.href} className={getLinkClass(link.key)}>
                {link.label}
              </Link>
            )
          )}
        </div>

        <div className="flex items-center justify-center md:mx-auto">
          <Link
            href="/"
            className="flex items-center cursor-pointer shrink-0 hover:opacity-80 transition-opacity"
          >
            <Image
              src={logoSrc}
              alt="Justisio"
              width={110}
              height={30}
              className="h-auto w-auto"
            />
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-4 lg:gap-6 justify-end">
          <Link
            href={isAuthenticated ? '/id/home' : '/login'}
            className={`text-sm font-medium ${textColor} transition-colors`}
          >
            Masuk
          </Link>
          <Link
            href="/id/home"
            className="px-4 lg:px-5 py-2 rounded-full text-xs lg:text-sm font-bold transition-all bg-navy-900 text-white hover:bg-navy-800 flex items-center gap-1 group/btn"
            onClick={() => analytics.landingPage.navbarCTAClicked({ label: 'Mulai Riset (Desktop)' })}
          >
            Mulai Riset
            <ChevronDown size={14} className="rotate-[270deg] transition-transform group-hover/btn:translate-x-0.5" />
          </Link>
        </div>

        <div className="md:hidden flex items-center">
          <button
            className={`w-10 h-10 flex items-center justify-center rounded-lg ${mobileMenuText} ${mobileMenuHover} transition-colors`}
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={24} strokeWidth={2} /> : <Menu size={24} strokeWidth={2} />}
          </button>
        </div>
      </nav>

      <MobileMenu isOpen={isOpen} onClose={() => setIsOpen(false)} isTransparent={isTransparent} />
    </>
  );
}
